import * as angular from "angular";
import { AdminCampaignService } from "../../../services/admin-campaign.service";
import { ApiUserService } from "../../../services/user.service";
import { OrganisationService } from "../../../services/organisation.service";
import { DialogUtils } from "../../../helper/dialog.helper";
import { ToastUtils } from "../../../helper/toast.helper";
import { Filter } from "../../../models/filter.model";
import { CampaignDataProvider } from "../../../data-provider/campaign.dp";

interface ExportColumn {
    id: string;
    name: string;
    selected: boolean;
    value: (row: any) => any;
    /** Section this column is filed under; blank means ungrouped. */
    group?: string;
}

/** A section of the column picker, built from the columns themselves. */
interface ExportColumnGroup {
    name: string;
    columns: ExportColumn[];
}

/**
 * AV-17962: export the current admin tab to CSV. Rows come from the same
 * list endpoints with an export flag that drops the page limit, so the file
 * contains exactly what the current filters show. Columns are chosen here,
 * Database-export style.
 */
let AdminExportDialogComponent = {
    selector: "vcAdminExportDialog",
    templateUrl:
        "/angular/components/admin/dialogs/admin-export-dialog.component.html",
    bindings: {
        tab: "<",
        filter: "<",
    },
    controller: class AdminExportDialogController {
        // * bindings for typescript compiler *
        public tab: string;
        public filter: Filter;
        // ************************************

        public columns: ExportColumn[] = [];
        /**
         * Built once from columns in $onInit. A function returning fresh
         * arrays from the template would give ng-repeat new identities on
         * every digest and re-render without end.
         */
        public groups: ExportColumnGroup[] = [];
        public loading = false;

        private dialog: DialogUtils;
        private toast: ToastUtils;
        private campaignService: AdminCampaignService;
        private userService: ApiUserService;
        private organisationService: OrganisationService;
        private campaignDataProvider: CampaignDataProvider;
        private readonly $window: any;
        private kinds: any[] = [];

        constructor(
            AdminCampaignService: AdminCampaignService,
            ApiUserService: ApiUserService,
            OrganisationService: OrganisationService,
            CampaignDataProvider: CampaignDataProvider,
            DialogUtils: DialogUtils,
            ToastUtils: ToastUtils,
            $window: any
        ) {
            this.campaignDataProvider = CampaignDataProvider;
            this.campaignService = AdminCampaignService;
            this.userService = ApiUserService;
            this.organisationService = OrganisationService;
            this.dialog = DialogUtils;
            this.toast = ToastUtils;
            this.$window = $window;
        }

        $onInit(): void {
            if (this.tab == "campaigns") {
                this.campaignDataProvider
                    .getKinds()
                    .then((kinds: any[]) => (this.kinds = kinds));
            }
            // Per the ticket: all columns included by default - the
            // checkboxes are for opting out, not in.
            // Every column starts selected; the checkboxes are for opting out.
            const groups = AdminExportDialogController.COLUMN_GROUPS[this.tab];
            this.columns = this.columnsForTab(this.tab).map(
                (column: ExportColumn): ExportColumn => ({
                    ...column,
                    selected: true,
                    group: groups ? groups[column.id] : undefined,
                })
            );
            this.groups = this.buildGroups();
        }

        title(): string {
            if (this.tab == "campaigns") return "Export Accounts";
            if (this.tab == "organisations") return "Export Organizations";
            return "Export Users";
        }

        /** Singular noun for the "export every <subject>" warning. */
        subject(): string {
            if (this.tab == "campaigns") return "account";
            if (this.tab == "organisations") return "organisation";
            return "user";
        }

        /**
         * Nothing narrows the list - no search, no quick filter (status
         * pills, region/active switches, Accounts I'm On) and no filter
         * pane values - so the export walks every row in the system for
         * this tab, which is slow on Accounts and rarely what the admin
         * meant.
         */
        unfiltered(): boolean {
            // Organisations: small unpaginated list, exporting everything
            // is the normal case - no warning (AV-17981)
            if (this.tab == "organisations") {
                return false;
            }
            if (!this.filter) {
                return true;
            }
            const filter: any = this.filter;
            if (filter.search && filter.search.values) {
                return false;
            }
            if (
                filter.attached ||
                filter.thisRegion ||
                filter.onlineOnly ||
                filter.accountStatus ||
                filter.platform ||
                filter.ostatus ||
                filter.result ||
                filter.statusGroup ||
                filter.active
            ) {
                // Quick filters are plain booleans/strings, which
                // nonEmptyValues() drops, but they do narrow the export.
                return false;
            }
            if (
                this.filter.status &&
                this.filter.status.values &&
                this.filter.status.values.length
            ) {
                // Status chips write straight onto the applied filter.
                return false;
            }
            if (angular.isFunction(filter.countNonEmptyFilters)) {
                return filter.countNonEmptyFilters() == 0;
            }
            return true;
        }

        toggleAll(state: boolean): void {
            angular.forEach(this.columns, (column) => {
                column.selected = state;
            });
        }

        selectedCount(): number {
            return this.columns.filter((column) => column.selected).length;
        }

        /** The columns as sections, in GROUP_ORDER; unlisted names last. */
        private buildGroups(): ExportColumnGroup[] {
            const order = AdminExportDialogController.GROUP_ORDER;
            const seen: { [name: string]: ExportColumn[] } = {};

            angular.forEach(this.columns, (column: ExportColumn) => {
                const name = column.group || "";
                seen[name] = seen[name] || [];
                seen[name].push(column);
            });

            return Object.keys(seen)
                .sort((a: string, b: string): number => {
                    const ai = order.indexOf(a);
                    const bi = order.indexOf(b);
                    return (
                        (ai == -1 ? order.length : ai) -
                        (bi == -1 ? order.length : bi)
                    );
                })
                .map(
                    (name: string): ExportColumnGroup => ({
                        name: name,
                        columns: seen[name],
                    })
                );
        }

        groupSelectedCount(group: ExportColumnGroup): number {
            return group.columns.filter(
                (column: ExportColumn): boolean => column.selected
            ).length;
        }

        /** One control per section: all on, or all off. */
        toggleGroup(group: ExportColumnGroup): void {
            const turnOn =
                this.groupSelectedCount(group) < group.columns.length;
            angular.forEach(group.columns, (column: ExportColumn) => {
                column.selected = turnOn;
            });
        }

        groupToggleLabel(group: ExportColumnGroup): string {
            return this.groupSelectedCount(group) < group.columns.length
                ? "All"
                : "None";
        }

        cancel(): void {
            this.dialog.cancel();
        }

        exportCsv(): void {
            if (this.loading || this.selectedCount() == 0) {
                return;
            }
            this.loading = true;
            this.fetchRows().then(
                (rows: any[]) => {
                    this.loading = false;
                    this.download(this.buildCsv(rows));
                    this.toast.success(rows.length + " rows exported");
                    this.dialog.hide();
                },
                () => {
                    this.loading = false;
                    this.toast.error();
                }
            );
        }

        private fetchRows(): Promise<any[]> {
            if (this.tab == "campaigns") {
                return (
                    this.campaignService
                        // Same quick-filter flags the Accounts list sends, so the
                        // file matches what is on screen. exportAll is last.
                        .all(
                            this.filter.nonEmptyValues(),
                            null,
                            undefined,
                            this.filter.attached,
                            this.filter.thisRegion,
                            true,
                            this.filter.sort,
                            this.filter.sortDir,
                            this.filter.lens == "activity"
                                ? {
                                      lens: "activity",
                                      range: this.filter.range || 7,
                                      trend: this.filter.trend,
                                  }
                                : null
                        )
                        .then((response: object) => response["data"].data ?? [])
                );
            }
            if (this.tab == "organisations") {
                // The organisations endpoint is unpaginated already; same
                // params the list sends.
                return this.organisationService
                    .admin(this.filter)
                    .then((response: object) => response["data"].data ?? []);
            }
            return this.userService
                .all({ filter: this.filter, export: true })
                .then((response: object) => response["data"].data ?? []);
        }

        private buildCsv(rows: any[]): string {
            const selected = this.columns.filter((column) => column.selected);
            const escape = (value: any): string => {
                let text =
                    value === null || angular.isUndefined(value)
                        ? ""
                        : String(value);
                // Names are user-controlled. A leading = + - @ (or tab/CR)
                // is executed as a formula by Excel/Sheets on open, so
                // neutralise it with a leading apostrophe.
                if (/^[=+\-@\t\r]/.test(text)) {
                    text = "'" + text;
                }
                return '"' + text.replace(/"/g, '""') + '"';
            };
            const lines = [
                selected.map((column) => escape(column.name)).join(","),
            ];
            angular.forEach(rows, (row) => {
                lines.push(
                    selected
                        .map((column) => escape(column.value(row)))
                        .join(",")
                );
            });
            // BOM so Excel opens UTF-8 names correctly
            return "\uFEFF" + lines.join("\r\n");
        }

        private download(csv: string): void {
            // Local date + time, filesystem-safe: accounts-2026-09-08-10.19.32
            const now = new Date();
            const pad = (n: number): string => String(n).padStart(2, "0");
            const stamp =
                now.getFullYear() +
                "-" +
                pad(now.getMonth() + 1) +
                "-" +
                pad(now.getDate()) +
                "-" +
                pad(now.getHours()) +
                "." +
                pad(now.getMinutes()) +
                "." +
                pad(now.getSeconds());
            const word =
                this.tab == "campaigns"
                    ? "accounts"
                    : this.tab == "organisations"
                    ? "organizations"
                    : "users";
            const name = word + "-" + stamp + ".csv";
            const blob = new Blob([csv], {
                type: "text/csv;charset=utf-8;",
            });
            const link = document.createElement("a");
            link.href = this.$window.URL.createObjectURL(blob);
            link.download = name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            this.$window.URL.revokeObjectURL(link.href);
        }

        /** The panel's Type cell, split by attribute (AV-17982). */
        /** Stripe (subscription), Xero (linked), Organisation (sub-account), or blank. */
        private billingPlatform(r: any): string {
            if (r.subscriptionid && r.subscriptionid != "none") return "Stripe";
            if (r.oid) return "Organisation";
            if (r.xero_status == "matched") return "Xero";
            return "";
        }

        /** Xero standing in Stripe's vocabulary: cancelled (archived), past due, active. */
        private xeroStanding(r: any): string {
            if (r.xero_status != "matched") return "";
            if (r.xero_contact_status == "ARCHIVED") return "cancelled";
            if (Number(r.xero_overdue) > 0) return "past due";
            return "active";
        }

        /** Stats arrive nested on the list rows and flat on the export rows. */
        private statValue(row: any, key: string): any {
            if (row.stats && row.stats[key] !== undefined) {
                return row.stats[key];
            }
            return row["stats_" + key];
        }

        private kindString(id: string, attribute: string): string {
            for (let i = 0; i < this.kinds.length; i++) {
                const kind = this.kinds[i];
                const selected = kind.subcategories.find(
                    (subcategory: any) => `${subcategory.id}` === `${id}`
                );
                if (selected) {
                    return attribute == "category" ? kind.name : selected.name;
                }
            }
            return "";
        }

        /** "used of limit" the way vc-limit-used renders it. */
        private limitString(used: any, included?: any, extra?: any): string {
            const usedNum = used ?? "";
            const total = (parseInt(included) || 0) + (parseInt(extra) || 0);
            return total > 0 ? usedNum + " of " + total : String(usedNum);
        }

        /** Activity lens (AV-17958): the file mirrors the activity columns. */
        private activityColumns(tab: string): ExportColumn[] {
            const col = (
                id: string,
                name: string,
                value: (r: any) => any
            ): ExportColumn => ({
                id,
                name,
                selected: true,
                value,
            });
            const act = (r: any): any => r.activity || {};
            const label = (r: any): string => act(r).rangeLabel || "";
            const head: ExportColumn[] =
                tab == "organisations"
                    ? [
                          col("oid", "ID", (r) => r.oid),
                          col("oname", "Name", (r) => r.oname),
                          col("uemail", "Owner Email", (r) => r.uemail),
                          col(
                              "accounts",
                              "Accounts",
                              (r) => act(r).accountCount
                          ),
                          col(
                              "active_accounts",
                              "Active accounts",
                              (r) => act(r).activeAccountCount
                          ),
                      ]
                    : [
                          col("campaignid", "ID", (r) => r.campaignid),
                          col("campaignname", "Name", (r) => r.campaignname),
                          col("oname", "Organisation", (r) => r.oname),
                      ];
            return head.concat([
                col("range", "Window", (r) => label(r)),
                col("interactions", "Interactions", (r) => act(r).interactions),
                col(
                    "interactions_delta",
                    "Interactions change %",
                    (r) => act(r).interactionsDelta
                ),
                col("logins", "Logins", (r) => act(r).logins),
                col(
                    "logins_delta",
                    "Logins change %",
                    (r) => act(r).loginsDelta
                ),
                col("active_users", "Active users", (r) => act(r).activeUsers),
                col(
                    "total_users",
                    tab == "organisations" ? "User seats" : "Users",
                    (r) => act(r).totalUsers
                ),
                col(
                    "contacts_added",
                    "Contacts added",
                    (r) => act(r).contactsAdded
                ),
                col(
                    "last_activity",
                    "Last activity",
                    (r) => act(r).lastActivityAt
                ),
                col("trend", "Trend", (r) => act(r).trend || "nodata"),
            ]);
        }

        /**
         * Which section each column belongs to, by tab. Kept beside the
         * column lists rather than on every col() call so the definitions
         * stay readable, and per tab because the same id can mean different
         * things - stats_customfields is usage on an account and sits with
         * the limits on an organisation.
         */
        private static readonly COLUMN_GROUPS: {
            [tab: string]: { [id: string]: string };
        } = {
            campaigns: {
                campaignid: "Identity",
                campaignname: "Identity",
                type: "Identity",
                subtype: "Identity",
                owner: "Ownership",
                uemail: "Ownership",
                creator: "Ownership",
                creator_uemail: "Ownership",
                campaignstatusname: "Status",
                campaignregion: "Status",
                created: "Status",
                subscriptionid: "Billing",
                cpname: "Billing",
                cpprice: "Billing",
                subscriptionstatus: "Billing",
                billing: "Billing",
                xero_name: "Billing",
                xero_standing: "Billing",
                xero_outstanding: "Billing",
                xero_overdue: "Billing",
                xero_currency: "Billing",
                userslimit: "Usage",
                superuserslimit: "Usage",
                customfieldslimit: "Usage",
                houseoccupantslimit: "Usage",
                houses: "Usage",
                interactions: "Usage",
                planhouseoccupantslimit: "Usage",
                geocodinglimit: "Usage",
            },
            organisations: {
                oid: "Identity",
                oname: "Identity",
                owner: "Ownership",
                uemail: "Ownership",
                oregion: "Status",
                ostatus: "Status",
                otimestamp: "Status",
                xero_name: "Billing",
                xero_standing: "Billing",
                xero_outstanding: "Billing",
                xero_overdue: "Billing",
                xero_currency: "Billing",
                stats_users: "Usage",
                stats_superusers: "Usage",
                stats_houseoccupants: "Usage",
                houses: "Usage",
                interactions: "Usage",
                stats_geocoded: "Usage",
                users: "Limits",
                superusers: "Limits",
                houseoccupants: "Limits",
                stats_customfields: "Limits",
                customfields: "Limits",
            },
        };

        /** Section order; anything unlisted falls to the end, ungrouped. */
        private static readonly GROUP_ORDER: string[] = [
            "Identity",
            "Ownership",
            "Status",
            "Billing",
            "Usage",
            "Limits",
        ];

        private columnsForTab(tab: string): ExportColumn[] {
            if (
                this.filter &&
                this.filter.lens == "activity" &&
                (tab == "campaigns" || tab == "organisations")
            ) {
                return this.activityColumns(tab);
            }
            const col = (
                id: string,
                name: string,
                selected: boolean,
                value: (row: any) => any,
                group?: string
            ): ExportColumn => ({ id, name, selected, value, group });

            if (tab == "campaigns") {
                // Mirrors the Accounts tab columns (AV-17982), with the
                // panel's combined cells split for a flat file: Owner into
                // Owner/Owner Email, Plan into Plan/Plan Price, Type into
                // Type/Sub Type, Creator into Creator/Creator Email.
                return [
                    col("campaignid", "ID", true, (r) => r.campaignid),
                    col("campaignname", "Name", true, (r) => r.campaignname),
                    col("owner", "Owner", true, (r) =>
                        ((r.ufname ?? "") + " " + (r.ulname ?? "")).trim()
                    ),
                    col("uemail", "Owner Email", true, (r) => r.uemail),
                    col(
                        "subscriptionid",
                        "Subscription ID",
                        true,
                        (r) => r.subscriptionid
                    ),
                    col("cpname", "Plan", true, (r) => r.cpname),
                    col("cpprice", "Plan Price", true, (r) => r.cpprice),
                    col(
                        "subscriptionstatus",
                        "Stripe Status",
                        true,
                        (r) => r.subscriptionstatus
                    ),
                    // AV-17974: which platform bills the account, and the Xero link
                    col("billing", "Billing", true, (r) =>
                        this.billingPlatform(r)
                    ),
                    col("xero_name", "Xero Contact", true, (r) =>
                        r.xero_status == "matched" ? r.xero_name : ""
                    ),
                    col("xero_standing", "Xero Status", true, (r) =>
                        this.xeroStanding(r)
                    ),
                    col("xero_outstanding", "Xero Outstanding", true, (r) =>
                        r.xero_status == "matched" ? r.xero_outstanding : ""
                    ),
                    col("xero_overdue", "Xero Overdue", true, (r) =>
                        r.xero_status == "matched" ? r.xero_overdue : ""
                    ),
                    col("xero_currency", "Xero Currency", true, (r) =>
                        r.xero_status == "matched" ? r.xero_currency : ""
                    ),
                    col("type", "Type", true, (r) =>
                        this.kindString(r.campaignkind, "category")
                    ),
                    col("subtype", "Sub Type", true, (r) =>
                        this.kindString(r.campaignkind, "subcategory")
                    ),
                    col(
                        "campaignstatusname",
                        "Status",
                        true,
                        (r) => r.campaignstatusname
                    ),
                    col(
                        "campaignregion",
                        "Region",
                        true,
                        (r) => r.campaignregion
                    ),
                    col(
                        "created",
                        "Created",
                        true,
                        (r) => r.campaigncreationdatetime
                    ),
                    col("creator", "Creator", true, (r) =>
                        (
                            (r.creator_ufname ?? "") +
                            " " +
                            (r.creator_ulname ?? "")
                        ).trim()
                    ),
                    col(
                        "creator_uemail",
                        "Creator Email",
                        true,
                        (r) => r.creator_uemail
                    ),
                    col("userslimit", "Standard Users", true, (r) =>
                        this.limitString(
                            r.stats?.users ?? r.stats_users,
                            r.limits?.users,
                            r.limits?.extrausers
                        )
                    ),
                    col("superuserslimit", "Super Users", true, (r) =>
                        this.limitString(
                            r.stats?.superusers ?? r.stats_superusers,
                            r.limits?.superusers,
                            r.limits?.extrasuperusers
                        )
                    ),
                    col("customfieldslimit", "Custom Fields", true, (r) =>
                        this.limitString(
                            r.stats?.customfields,
                            r.limits?.customfields,
                            r.limits?.extracustomfields
                        )
                    ),
                    col("houseoccupantslimit", "Contacts", true, (r) =>
                        this.limitString(
                            r.stats?.houseoccupants ?? r.stats_houseoccupants,
                            r.limits?.houseoccupants
                        )
                    ),
                    col("houses", "Houses", true, (r) =>
                        this.statValue(r, "houses")
                    ),
                    col("interactions", "Interactions", true, (r) =>
                        this.statValue(r, "interactions")
                    ),
                    col("planhouseoccupantslimit", "Plan Contacts", true, (r) =>
                        this.limitString(
                            r.stats?.houseoccupants ?? r.stats_houseoccupants,
                            r.plan?.cpmax
                        )
                    ),
                    col(
                        "geocodinglimit",
                        "Geocoded Houses",
                        true,
                        (r) => r.stats?.geocoded
                    ),
                ];
            }
            if (tab == "organisations") {
                return [
                    col("oid", "ID", true, (r) => r.oid),
                    col("oname", "Name", true, (r) => r.oname),
                    col("owner", "Owner", true, (r) =>
                        ((r.ufname ?? "") + " " + (r.ulname ?? "")).trim()
                    ),
                    col("uemail", "Owner Email", true, (r) => r.uemail),
                    col("oregion", "Region", true, (r) => r.oregion),
                    col("ostatus", "Status", true, (r) => r.ostatus),
                    // AV-17982: the file carries every column the list shows
                    col("otimestamp", "Created", true, (r) => r.otimestamp),
                    // AV-17974: Xero link on the organisation
                    col("xero_name", "Xero Contact", true, (r) =>
                        r.xero_status == "matched" ? r.xero_name : ""
                    ),
                    col("xero_standing", "Xero Status", true, (r) =>
                        this.xeroStanding(r)
                    ),
                    col("xero_outstanding", "Xero Outstanding", true, (r) =>
                        r.xero_status == "matched" ? r.xero_outstanding : ""
                    ),
                    col("xero_overdue", "Xero Overdue", true, (r) =>
                        r.xero_status == "matched" ? r.xero_overdue : ""
                    ),
                    col("xero_currency", "Xero Currency", true, (r) =>
                        r.xero_status == "matched" ? r.xero_currency : ""
                    ),
                    col("stats_users", "Users", true, (r) => r.stats_users),
                    col(
                        "stats_superusers",
                        "Super Users",
                        false,
                        (r) => r.stats_superusers
                    ),
                    col(
                        "stats_houseoccupants",
                        "Contacts",
                        false,
                        (r) => r.stats_houseoccupants
                    ),
                    col("houses", "Houses", true, (r) =>
                        this.statValue(r, "houses")
                    ),
                    col("interactions", "Interactions", true, (r) =>
                        this.statValue(r, "interactions")
                    ),
                    col("users", "User Limit", true, (r) => r.users),
                    col(
                        "superusers",
                        "Super User Limit",
                        false,
                        (r) => r.superusers
                    ),
                    col(
                        "houseoccupants",
                        "Contact Limit",
                        false,
                        (r) => r.houseoccupants
                    ),
                    col(
                        "stats_customfields",
                        "Custom Fields",
                        false,
                        (r) => r.stats_customfields
                    ),
                    col(
                        "customfields",
                        "Custom Field Limit",
                        false,
                        (r) => r.customfields
                    ),
                    col(
                        "stats_geocoded",
                        "Geocoded Houses",
                        false,
                        (r) => r.stats_geocoded
                    ),
                ];
            }
            return [
                col("uid", "ID", true, (r) => r.uid),
                col("name", "Name", true, (r) =>
                    ((r.ufname ?? "") + " " + (r.ulname ?? "")).trim()
                ),
                col("uemail", "Email", true, (r) => r.uemail),
                col("uphonenumber", "Phone", true, (r) =>
                    r.uphonenumber
                        ? ((r.ucountrycode ?? "") + " " + r.uphonenumber).trim()
                        : ""
                ),
                col(
                    "uaccountstatus",
                    "Account Status",
                    true,
                    (r) => r.uaccountstatus
                ),
                col("udevice", "Last Device", true, (r) =>
                    r.udevice ? r.udevice + (r.uos ? " - " + r.uos : "") : ""
                ),
                col("ujoindate", "Joined", true, (r) => r.ujoindate),
                col("uipaddress", "IP Address", true, (r) => r.uipaddress),
            ];
        }
    },
};

angular
    .module("admin")
    .component(AdminExportDialogComponent.selector, AdminExportDialogComponent);
